// Must be imported before antd is used anywhere: patches antd v5's static
// methods (message, notification, Modal.confirm/…) to render under React 19.
// Without it those static calls silently no-op — e.g. the email-triage send
// confirmation never opens, so "Senden" fires no request and shows no toast.
import '@ant-design/v5-patch-for-react-19';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// Disable verbose console.log for cleaner console and better performance
// Set REACT_APP_DEBUG=true in .env to enable debug logs in development
const enableDebugLogs = process.env.REACT_APP_DEBUG === 'true';

if (process.env.NODE_ENV === 'production' || !enableDebugLogs) {
  console.log = () => {};
  console.debug = () => {};
  // Keep console.warn and console.error for important messages
}

// --- Browser auto-translation guard ----------------------------------------
// Chrome / Google Translate rewrites text nodes in place. React keeps stale
// references to the original nodes, so its next insertBefore/removeChild call
// targets a node the translator already moved, throwing:
//   "NotFoundError: Failed to execute 'insertBefore'/'removeChild' on 'Node':
//    The node ... is not a child of this node."
// Turning these into no-ops when the parent doesn't match keeps the app alive
// instead of crashing the whole page for translated users.
// Ref: https://github.com/facebook/react/issues/11538#issuecomment-417504600
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (this: Node, child: any) {
    if (child?.parentNode !== this) {
      console.warn('🌐 Suppressed removeChild from a mismatched parent (likely browser translation).');
      return child;
    }
    return originalRemoveChild.call(this, child);
  } as typeof Node.prototype.removeChild;

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (this: Node, newNode: any, referenceNode: any) {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn('🌐 Suppressed insertBefore with a mismatched reference node (likely browser translation).');
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  } as typeof Node.prototype.insertBefore;
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
